import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';
import { listInstanceCapabilities, setInstanceCapability } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Per-instance capability toggles (CT-4190). Optimistic set with a submittingRef
 * guard; toggling calls the API immediately (mock). Race/unmount-safe like
 * useInstanceUsage — clears the prior error before each load so a successful
 * reload after a failure recovers instead of pinning the error StatusMessage.
 */
export function useInstanceCapabilities(instanceId) {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const [caps, setCaps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await listInstanceCapabilities(instanceId);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setCaps(validateApiResponse(res, t('common.requestFailed')).body ?? []);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [instanceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback(
    async (capId, enabled) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      // Optimistic update.
      setCaps((prev) => prev.map((c) => (c.id === capId ? { ...c, enabled } : c)));
      try {
        await setInstanceCapability(instanceId, capId, enabled);
      } catch (err) {
        if (!mountedRef.current) return;
        setCaps((prev) => prev.map((c) => (c.id === capId ? { ...c, enabled: !enabled } : c)));
        toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
      } finally {
        submittingRef.current = false;
      }
    },
    [instanceId, t, toast],
  );

  const grouped = useMemo(() => {
    const map = { tool: [], data_sync: [], mcp: [] };
    for (const c of caps) (map[c.type] ?? (map[c.type] = [])).push(c);
    return map;
  }, [caps]);

  return useMemo(
    () => ({ caps, grouped, isLoading, error, toggle }),
    [caps, grouped, isLoading, error, toggle],
  );
}
